// Ask TTO — the admin's AI assistant page (Oct 11, 2026 — Dennis).
// Type a question about how organizations and players use TTO; the server answers it
// from the production database, read-only (backend services/adminAskService.js), and
// shows how it found the answer. Admin dashboard only — organizers never see this.
import React, { useEffect, useRef, useState } from 'react';

// Dennis's own examples (Oct 11, 2026).
const EXAMPLES = [
  'How many players at the Friday group signed up themselves vs. were added by the organizer?',
  'What groups are playing today?',
  'Who is using the live-scoring app?',
  'What future events are on the books across all orgs?',
  'Who is using TTO the most?',
  "Which subscribers haven't added a tee time for a while?",
  "Whose subscription is expiring soon?",
  'How many new subscribers last week?',
  'Which free subscriptions turned into paid subscriptions?',
  'Who sent messages to their players recently?',
  'Are any emails across all orgs bouncing repeatedly?',
  'Is any org changing players to guests and having them play often (gaming the player limit)?',
  'Is Dennis Samuelson using live scoring often?',
  "Which free subscriptions haven't used TTO in a while?",
];

// A small reader for the answer's markdown: tables, bullets, headings, **bold**.
function inline(text, key) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (p.startsWith('**') && p.endsWith('**') ? <strong key={`${key}-${i}`}>{p.slice(2, -2)}</strong> : <React.Fragment key={`${key}-${i}`}>{p}</React.Fragment>));
}
function Answer({ text }) {
  const lines = String(text || '').split('\n');
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*\|/.test(line)) {
      const block = [];
      while (i < lines.length && /^\s*\|/.test(lines[i])) block.push(lines[i++]);
      i--;
      const rows = block.filter(l => !/^\s*\|?\s*:?-{2,}/.test(l)).map(l => l.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim()));
      const [head, ...body] = rows;
      out.push(
        <div key={`t${i}`} style={{ overflowX: 'auto', margin: '8px 0' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 14 }}>
            {head && <thead><tr>{head.map((c, j) => <th key={j} style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid #d1d5db', background: '#f9fafb', whiteSpace: 'nowrap' }}>{inline(c, `h${j}`)}</th>)}</tr></thead>}
            <tbody>{body.map((r, k) => <tr key={k}>{r.map((c, j) => <td key={j} style={{ padding: '6px 10px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>{inline(c, `c${k}${j}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
    } else if (/^\s*[-*•]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*[-*•]\s+/, ''));
      i--;
      out.push(<ul key={`u${i}`} style={{ margin: '6px 0', paddingLeft: 22 }}>{items.map((it, k) => <li key={k} style={{ margin: '3px 0' }}>{inline(it, `l${i}${k}`)}</li>)}</ul>);
    } else if (/^#{1,4}\s+/.test(line)) {
      out.push(<div key={`h${i}`} style={{ fontWeight: 700, fontSize: 15, margin: '10px 0 4px' }}>{inline(line.replace(/^#{1,4}\s+/, ''), `hh${i}`)}</div>);
    } else if (line.trim() === '') {
      out.push(<div key={`b${i}`} style={{ height: 8 }} />);
    } else {
      out.push(<div key={`p${i}`} style={{ lineHeight: 1.5 }}>{inline(line, `p${i}`)}</div>);
    }
  }
  return <div style={{ fontSize: 15, color: '#1f2937' }}>{out}</div>;
}

function Steps({ steps }) {
  const [open, setOpen] = useState(false);
  if (!steps || steps.length === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, padding: 0 }}>
        {open ? '▾ Hide how it found this' : `▸ Show how it found this (${steps.length} ${steps.length === 1 ? 'look-up' : 'look-ups'})`}
      </button>
      {open && steps.map((s, i) => (
        <div key={i} style={{ marginTop: 8, padding: '8px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
            {s.tool === 'sendgrid_suppressions' ? `SendGrid ${s.kind} list` : (s.purpose || 'Database query')}
            {s.error ? <span style={{ color: '#b91c1c' }}> · refused or failed: {s.error}</span> : ` · ${s.row_count ?? 0} row${s.row_count === 1 ? '' : 's'}${s.truncated ? ' (first 200 shown to the assistant)' : ''}`}
          </div>
          {s.sql && <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#111827' }}>{s.sql}</pre>}
        </div>
      ))}
    </div>
  );
}

export default function AdminAsk({ adminFetch }) {
  const [question, setQuestion] = useState('');
  const [items, setItems] = useState([]);          // [{ q, a, steps, error, at }]
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { if (endRef.current && endRef.current.scrollIntoView) endRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [items, busy]);

  const ask = async (text) => {
    const q = String(text || question).trim();
    if (!q || busy) return;
    setBusy(true); setQuestion('');
    const history = items.filter(x => x.a).slice(-6).map(x => ({ q: x.q, a: x.a }));
    try {
      const r = await adminFetch('/admin/ask', { method: 'POST', body: JSON.stringify({ question: q, history }) });
      const d = r ? await r.json().catch(() => ({})) : {};
      if (r && r.ok) setItems(xs => [...xs, { q, a: d.answer, steps: d.steps || [], at: new Date() }]);
      else setItems(xs => [...xs, { q, error: d.error || 'Something went wrong.', at: new Date() }]);
    } catch (e) {
      setItems(xs => [...xs, { q, error: 'Could not reach the server.', at: new Date() }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }} data-testid="admin-ask">
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1e3a5f', margin: '0 0 4px' }}>🔎 Ask TTO</h2>
        <div style={{ fontSize: 14, color: '#6b7280' }}>
          Ask anything about how organizations and players use TTO. Answers come from the live database (read-only) and SendGrid's bounce lists. Admin only.
        </div>
      </div>

      {items.length === 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Try one</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {EXAMPLES.map(ex => (
              <button key={ex} type="button" onClick={() => ask(ex)} disabled={busy}
                style={{ textAlign: 'left', fontSize: 13, padding: '8px 12px', borderRadius: 999, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}

      {items.map((it, i) => (
        <div key={i} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
            <div style={{ maxWidth: '80%', background: '#1e3a5f', color: '#fff', padding: '10px 14px', borderRadius: '14px 14px 4px 14px', fontSize: 15 }}>{it.q}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px 14px 14px 4px', padding: '12px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            {it.error ? <div style={{ color: '#b91c1c', fontSize: 14 }}>⚠️ {it.error}</div> : <Answer text={it.a} />}
            <Steps steps={it.steps} />
          </div>
        </div>
      ))}

      {busy && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '12px 16px', color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
          Checking the data… (this can take 10–30 seconds)
        </div>
      )}
      <div ref={endRef} />

      <form onSubmit={(e) => { e.preventDefault(); ask(); }} style={{ display: 'flex', gap: 8, position: 'sticky', bottom: 0, background: '#f3f4f6', padding: '10px 0' }}>
        <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={2} disabled={busy}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
          placeholder={items.length ? 'Ask a follow-up, or a new question…' : 'Ask a question…'}
          style={{ flex: 1, padding: '10px 12px', fontSize: 15, border: '2px solid #d1d5db', borderRadius: 10, resize: 'vertical', fontFamily: 'inherit' }} />
        <button type="submit" disabled={busy || !question.trim()}
          style={{ padding: '0 22px', borderRadius: 10, border: 'none', background: busy || !question.trim() ? '#9ca3af' : '#1e3a5f', color: '#fff', fontSize: 15, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
          Ask
        </button>
      </form>
      {items.length > 0 && (
        <button type="button" onClick={() => setItems([])} disabled={busy}
          style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, padding: 0 }}>Start over</button>
      )}
    </div>
  );
}
